import { BookCatalogEntry } from './booksData'

export type DiscoveryRowType =
  | 'mood'
  | 'trope'
  | 'personalized'
  | 'editorial'
  | 'contextual'

export interface DiscoveryRowFilters {
  genres?: string[] // match any genre in book.genres (case-insensitive)
  tags?: string[] // match ANY tag in book.tags
  tagsAll?: string[] // match ALL tags in book.tags
  spiceMin?: number // book.spiceLevel >= spiceMin
  spiceMax?: number // book.spiceLevel <= spiceMax
  darknessMin?: number // book.darknessLevel >= darknessMin
  darknessMax?: number // book.darknessLevel <= darknessMax
  maxReviewCount?: number // for "hidden gems"
  isNew?: boolean // for "new releases"
}

export interface DiscoveryRow {
  id: string
  title: string
  description?: string
  emoji?: string
  type: DiscoveryRowType
  filters: DiscoveryRowFilters
  requiresAuth?: boolean
  linkTo?: string
  accentColor?: string // Tailwind text color class for the icon/title
}

// ── Filter Engine ──────────────────────────────────────────────

export const getBooksForDiscoveryRow = (
  books: BookCatalogEntry[],
  filters: DiscoveryRowFilters,
): BookCatalogEntry[] => {
  return books.filter((book) => {
    // 1. Genre filter (match ANY)
    if (filters.genres && filters.genres.length > 0) {
      const bookGenres = book.genres.map((g) => g.toLowerCase())
      const primaryGenre = book.primaryGenre?.toLowerCase()
      const secondaryGenres =
        book.secondaryGenres?.map((g) => g.toLowerCase()) || []

      const hasGenre = filters.genres.some((g) => {
        const filterGenre = g.toLowerCase()
        return (
          bookGenres.includes(filterGenre) ||
          primaryGenre === filterGenre ||
          secondaryGenres.includes(filterGenre)
        )
      })
      if (!hasGenre) return false
    }

    // 2. Tags filter (match ANY)
    if (filters.tags && filters.tags.length > 0) {
      const bookTags = book.tags || []
      const hasTag = filters.tags.some((t) => bookTags.includes(t))
      if (!hasTag) return false
    }

    // 3. Tags filter (match ALL)
    if (filters.tagsAll && filters.tagsAll.length > 0) {
      const bookTags = book.tags || []
      const hasAllTags = filters.tagsAll.every((t) => bookTags.includes(t))
      if (!hasAllTags) return false
    }

    // 4. Spice level
    if (filters.spiceMin !== undefined && book.spiceLevel < filters.spiceMin)
      return false
    if (filters.spiceMax !== undefined && book.spiceLevel > filters.spiceMax)
      return false

    // 5. Darkness level
    if (
      filters.darknessMin !== undefined &&
      book.darknessLevel < filters.darknessMin
    )
      return false
    if (
      filters.darknessMax !== undefined &&
      book.darknessLevel > filters.darknessMax
    )
      return false

    // 6. Review count (Hidden Gems)
    if (filters.maxReviewCount !== undefined) {
      const reviews = book.reviewCount || 0
      if (reviews > filters.maxReviewCount) return false
    }

    // 7. New Releases
    if (filters.isNew !== undefined && book.isNew !== filters.isNew)
      return false

    return true
  })
}

// ── Row Definitions ───────────────────────────────────────────

export const defaultDiscoveryRows: DiscoveryRow[] = [
  {
    id: 'mood-dark-romance',
    title: 'Dark Romance Tonight',
    description: 'Intense, morally grey, and unapologetically dark.',
    emoji: '🌙',
    type: 'mood',
    filters: {
      genres: ['romance'],
      darknessMin: 3,
      spiceMin: 3,
    },
    linkTo: '/story-galaxy/node/romance-dark-romance',
    accentColor: 'text-purple-500',
  },
  {
    id: 'trope-enemies-to-lovers',
    title: 'Enemies to Lovers',
    description: 'From glaring across the room to sharing a bed.',
    emoji: '⚔️',
    type: 'trope',
    filters: {
      tags: ['enemies-to-lovers', 'rivals-to-lovers'],
    },
    linkTo: '/story-galaxy/node/engine-enemies-to-lovers',
    accentColor: 'text-rose-500',
  },
  {
    id: 'mood-cozy-fantasy',
    title: 'Cozy Fantasy Reads',
    description: 'Low stakes, high magic, and found family.',
    emoji: '☕',
    type: 'mood',
    filters: {
      genres: ['fantasy'],
      darknessMax: 2,
      tags: ['found-family', 'magic'],
    },
    linkTo: '/story-galaxy/node/fantasy-cozy-fantasy',
    accentColor: 'text-amber-500',
  },
  {
    id: 'editorial-hidden-gems',
    title: 'Hidden Gems Under 100 Reviews',
    description:
      'Discover these underrated masterpieces before everyone else does.',
    emoji: '💎',
    type: 'editorial',
    filters: {
      maxReviewCount: 100,
    },
    linkTo: '/browse?ratings=low',
    accentColor: 'text-teal-500',
  },
  {
    id: 'trope-slow-burn',
    title: 'Agonizing Slow Burns',
    description: 'The tension is thick enough to cut with a knife.',
    emoji: '🔥',
    type: 'trope',
    filters: {
      tags: ['slow-burn-trope'],
    },
    linkTo: '/story-galaxy/node/engine-slow-burn',
    accentColor: 'text-orange-500',
  },
  {
    id: 'contextual-new-releases',
    title: 'Fresh Off the Press',
    description: 'The latest releases across all genres.',
    emoji: '✨',
    type: 'contextual',
    filters: {
      isNew: true,
    },
    linkTo: '/browse?sort=new',
    accentColor: 'text-indigo-500',
  },
  {
    id: 'mood-emotional-damage',
    title: 'Emotionally Devastating',
    description: 'Keep the tissues handy for these tearjerkers.',
    emoji: '💔',
    type: 'mood',
    filters: {
      tags: ['grief-loss', 'angst'],
      darknessMin: 2,
    },
    accentColor: 'text-blue-500',
  },
]

export const aftertideDiscoveryRows: DiscoveryRow[] = [
  {
    id: 'at-mood-pitch-black',
    title: 'Pitch Black Romance',
    description: 'No limits, no boundaries. Enter the void.',
    emoji: '🖤',
    type: 'mood',
    filters: {
      genres: ['romance'],
      darknessMin: 4,
    },
    linkTo: '/browse?darkness=4,5',
    accentColor: 'text-gray-400',
  },
  {
    id: 'at-trope-monster',
    title: 'Monster & Paranormal',
    description: 'Why settle for human when you can have so much more?',
    emoji: '🐺',
    type: 'trope',
    filters: {
      tags: ['paranormal', 'monster-romance'],
    },
    linkTo: '/story-galaxy/node/romance-monster-romance',
    accentColor: 'text-[#c77dba]',
  },
  {
    id: 'at-trope-why-choose',
    title: 'Why Choose?',
    description: 'Because one is never enough.',
    emoji: '👑',
    type: 'trope',
    filters: {
      tags: ['why-choose', 'reverse-harem'],
    },
    linkTo: '/story-galaxy/node/engine-why-choose',
    accentColor: 'text-[#e2b04a]',
  },
  {
    id: 'at-editorial-hidden-gems',
    title: 'Undiscovered Nightmares',
    description: 'Hidden gems with fewer than 100 reviews.',
    emoji: '🩸',
    type: 'editorial',
    filters: {
      maxReviewCount: 100,
      darknessMin: 3,
    },
    linkTo: '/browse?ratings=low',
    accentColor: 'text-red-600',
  },
  {
    id: 'at-contextual-new',
    title: 'New Blood',
    description: 'Fresh releases in dark fiction.',
    emoji: '🔪',
    type: 'contextual',
    filters: {
      isNew: true,
      darknessMin: 3,
    },
    linkTo: '/browse?sort=new',
    accentColor: 'text-[#e2b04a]',
  },
]
